/** biome-ignore-all lint/nursery/useExplicitType: Zod schema inference */
import { z } from 'zod';
import type { IAPI } from '../../apiResponse';
import { createApiResponseSchema } from '../../apiResponse';
import { cefrLevelSchema } from '../enums/cefr-level';
import { challengeTypeSchema } from '../enums/challenge-type';
import { chunkTypeSchema } from '../enums/chunk-type';
import { frequencyBandSchema } from '../enums/frequency-band';
import { partOfSpeechSchema } from '../enums/part-of-speech';
import { registerSchema } from '../enums/register';
import { isBadExtraction } from '../is-bad-extraction';

function coerceArray(val: unknown): unknown {
  if (Array.isArray(val)) {
    return val;
  }
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      /* not valid JSON */
    }
  }
  return val;
}

const jsonStringArray = (max?: number) => {
  const base = z.preprocess(coerceArray, z.array(z.string()));
  return max ? base.pipe(z.array(z.string()).max(max)) : base;
};

const jsonArray = (max?: number) => {
  const base = z.preprocess(coerceArray, z.array(z.unknown()));
  return max ? base.pipe(z.array(z.unknown()).max(max)) : base;
};

const CONSECUTIVE_UNDERSCORES = /_{2,}/;

const exampleItemSchema = z
  .object({
    id: z.number(),
    text: z.string().refine((v) => !CONSECUTIVE_UNDERSCORES.test(v), {
      message: 'Example text must not contain blanks (underscores)',
    }),
    highlight: z.tuple([z.number(), z.number()]).optional(),
    context: z.string().optional(),
    difficulty: z.string().optional(),
  })
  .passthrough();

const senseItemSchema = z
  .object({
    definition: z.string(),
    partOfSpeech: z.string(),
    example: z.string().optional(),
  })
  .passthrough();

function coerceWordFamily(val: unknown): unknown {
  const parsed =
    typeof val === 'string'
      ? (() => {
          try {
            return JSON.parse(val);
          } catch {
            return val;
          }
        })()
      : val;
  if (Array.isArray(parsed)) {
    return { forms: parsed };
  }
  return parsed;
}

const wordFamilySchema = z.preprocess(
  coerceWordFamily,
  z.object({ forms: z.array(z.object({ word: z.string(), pos: z.string() })) }),
);

const examplesArray = z.preprocess(coerceArray, z.array(exampleItemSchema).max(10));
const sensesArray = z.preprocess(coerceArray, z.array(senseItemSchema).max(10));

const ipaString = z
  .string()
  .refine((v) => v.includes('/'), { message: 'IPA must contain /' })
  .nullish();

export const generatedChallengeFieldsObjectSchema = z.object({
  type: challengeTypeSchema,
  title: z.string(),
  definition: z.string().max(200),
  examples: examplesArray,
  difficulty: z.number().int().min(1).max(10),
  partOfSpeech: partOfSpeechSchema,
  cefrLevel: cefrLevelSchema,
  register: registerSchema,
  synonyms: jsonStringArray(5).nullish(),
  antonyms: jsonStringArray(5).nullish(),
  collocations: jsonStringArray(30).nullish(),
  commonMistakes: jsonStringArray(5).nullish(),
  usageNotes: z.string().nullish(),
  grammarPattern: z.string().nullish(),
  ipaUs: ipaString,
  ipaUk: ipaString,
  definitionSimple: z.string().max(200).nullish(),
  frequencyRank: z.number().int().positive().nullish(),
  frequencyBand: frequencyBandSchema.nullish(),
  variants: jsonStringArray(20).nullish(),
  mnemonicHint: z.string().nullish(),
  chunkType: chunkTypeSchema.nullish(),
  regionalVariation: z.string().nullish(),
  literalMeaning: z.string().nullish(),
  variableSlots: jsonArray(5).nullish(),
  additionalSenses: sensesArray.nullish(),
  wordFamily: wordFamilySchema.nullish(),
  etymology: z.string().nullish(),
  imagePrompt: z.string().max(500).nullish(),
  confidenceScore: z.number().int().min(1).max(10).nullish(),
});

export const generatedChallengeFieldsSchema = generatedChallengeFieldsObjectSchema.superRefine(
  (data, ctx) => {
    const reason = isBadExtraction(data.title, data.type);
    if (reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['title'],
        message: reason,
      });
    }
  },
);

export type IGeneratedChallengeFields = z.infer<typeof generatedChallengeFieldsSchema>;

export const generateChallengeResponseSchema = createApiResponseSchema(
  generatedChallengeFieldsSchema,
) as z.ZodSchema<IAPI<IGeneratedChallengeFields>>;

export type IGenerateChallengeResponse = IAPI<IGeneratedChallengeFields>;
